import React, { useState } from 'react';
import { Activity, Briefcase, Download, Plus, ChevronRight, Zap, Lock, TrendingUp, Shield, Crown, Palette, Monitor, Wand, CheckCircle2, Loader2, Settings, AlertTriangle, Trash2, DollarSign, CreditCard, Scale, Wallet } from 'lucide-react';
import CreateAccountModal from '../components/CreateAccountModal';
import { calculateCoreMetrics, calculateHealthStatus, calculateStochasticProjection } from '../components/financeEngine';

// 🔮 Interpreta os números da projeção em uma frase simples, pra quem não
// tem bagagem técnica entender de cara o que aquilo significa — sem
// precisar de outra chamada de IA (calculado na hora, sem custo extra).
function interpretarHorizonte(dado, meses) {
  if (!dado) return '';
  const faixa = dado.otimista - dado.pessimista;
  const faixaPct = dado.provavel !== 0 ? Math.abs(faixa / dado.provavel) : 0;
  if (dado.pessimista < 0) {
    return `Mesmo no cenário mais pessimista, o resultado pode ficar negativo em ${meses} meses — vale reforçar o caixa antes desse prazo.`;
  }
  if (faixaPct > 0.5) {
    return `A incerteza pra ${meses} meses é alta — mesmo no pior cenário, o resultado segue positivo, mas os números podem variar bastante.`;
  }
  if (faixaPct > 0.25) {
    return `Incerteza moderada pra ${meses} meses — o resultado tende a se manter estável, com variação razoável entre os cenários.`;
  }
  return `Boa previsibilidade pra ${meses} meses — os 3 cenários ficam próximos, sinal de negócio mais estável nesse prazo.`;
}

// 🔮 Interpreta a Matriz de Sensibilidade (9 cenários de Múltiplo x WACC)
// numa leitura em texto simples — mesmo princípio do interpretarHorizonte:
// calculado na hora, sem chamada de IA nem custo extra. Antes esse espaço
// (ao lado da matriz) ficava vazio/placeholder ("Inserir IA Aqui"); agora
// entrega uma leitura de verdade sobre o que os 9 cenários significam.
function interpretarSensibilidade({ apexMultiple, apexWacc, baseEbitda, debt, valorAtual, valorMax, valorMin }) {
  if (!valorMax || valorMax === 0) {
    return 'Nenhuma combinação razoável de múltiplo e WACC destrava valor nesse momento — a dívida consome todo o Enterprise Value em qualquer cenário. Reestruturação de capital é pré-requisito antes de qualquer negociação.';
  }
  const faixa = valorMax - valorMin;
  const faixaPct = valorAtual !== 0 ? faixa / valorAtual : 0;
  const zeraEmAlgumCenario = valorMin === 0;

  let leitura = '';
  if (zeraEmAlgumCenario) {
    leitura = `Nos cenários mais conservadores da matriz, o valuation zera — a estrutura de capital está no limite. `;
  } else if (faixaPct > 0.6) {
    leitura = `A variação entre o melhor e o pior cenário é grande (mais de 60%) — o valuation dessa empresa é altamente sensível a pequenas mudanças de múltiplo ou WACC. `;
  } else if (faixaPct > 0.3) {
    leitura = `Existe uma variação moderada entre os cenários — o valuation responde de forma razoável a mudanças de múltiplo e WACC, sem ser instável. `;
  } else {
    leitura = `Os 9 cenários ficam relativamente próximos — sinal de um valuation estável, pouco dependente de qual múltiplo ou WACC exato se usa na negociação. `;
  }

  // Qual das duas variáveis pesa mais? Compara o efeito de mover só o
  // múltiplo (mantendo WACC fixo) vs mover só o WACC (mantendo múltiplo fixo).
  const efeitoMultiplo = Math.max(0, (baseEbitda * (apexMultiple + 1) * 12) - Math.max(0, (baseEbitda * (apexMultiple - 1) * 12)));
  const efeitoWacc = Math.abs((baseEbitda * apexMultiple * 12 * (1 - ((apexWacc - 2) - 12.5) / 100)) - (baseEbitda * apexMultiple * 12 * (1 - ((apexWacc + 2) - 12.5) / 100)));
  const variavelDominante = efeitoMultiplo >= efeitoWacc ? 'o múltiplo de EBITDA negociado' : 'a taxa de WACC (custo de capital)';

  return `${leitura}Nessa empresa, ${variavelDominante} é o fator que mais move o valuation — é o ponto que mais vale a pena travar bem numa negociação de M&A.`;
}


// Mostra o valor atual + uma marca no ponto de referência (setor ou "neutro")
// + o quanto o usuário se afastou dela em %. Usado 2x (Múltiplo e WACC).
// ==========================================
function ApexSlider({ label, value, unit, min, max, step, onChange, reference, referenceLabel }) {
  const desvio = reference ? ((value - reference) / reference) * 100 : 0;
  const desvioColor = desvio > 0.5 ? 'text-lime-400' : desvio < -0.5 ? 'text-red-400' : 'text-slate-400';
  const refPct = reference != null ? Math.min(100, Math.max(0, ((reference - min) / (max - min)) * 100)) : null;

  return (
    <div className="bg-black/40 border border-white/5 p-5 rounded-xl">
      <div className="flex justify-between items-center mb-4">
        <label className="text-xs font-black uppercase tracking-widest text-slate-300">{label}</label>
        <span className="text-sm font-mono font-black text-blue-400 bg-blue-500/10 px-3 py-1 rounded-lg">{value}{unit}</span>
      </div>

      {/* Marca do ponto de referência acima da régua, pra não sobrar número solto */}
      <div className="relative h-4">
        {refPct !== null && (
          <div className="absolute -top-1 flex flex-col items-center" style={{ left: `${refPct}%`, transform: 'translateX(-50%)' }}>
            <span className="text-[11px] text-slate-500 font-bold whitespace-nowrap">▼ {reference.toFixed ? reference.toFixed(1) : reference}{unit}</span>
          </div>
        )}
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue-500 cursor-pointer" />

      {reference != null && (
        <div className="flex justify-between items-center mt-3 pt-2 border-t border-white/5">
          <span className="text-[11px] text-slate-500 uppercase tracking-wider">{referenceLabel}: <strong className="text-slate-300">{reference.toFixed ? reference.toFixed(1) : reference}{unit}</strong></span>
          <span className={`text-[11px] font-bold ${desvioColor}`}>{desvio > 0 ? '+' : ''}{desvio.toFixed(1)}% vs. ref.</span>
        </div>
      )}
    </div>
  );
}

export default function ConsultorReportTabs({
  reportTab,
  setReportTab,
  formData,
  report,
  finance,
  dynamicInsights,
  parecerIATexto,
  leituraProjecaoCurto,
  leituraProjecaoMedio,
  leituraProjecaoLongo,
  oportunidadesTexto,
  carregandoParecer,
  iaGerando,
  formatarParecerIA,
  formatCurrency,
  setShowDownloadConfirm,
  userPlan = 'free',
  historyList = [],
  loadDossier,
  onUpgradeRequest,
  onNewClient,
  onEditClient,
  onSyncDashboard,
  onGenerateSuperReport,
  apexMultiple,
  setApexMultiple,
  apexWacc,
  setApexWacc,
  setItemToDelete,
  // ⚠️ AQUI ESTÃO AS NOVAS PROPS (Recebidas do MobileInterface)
  wlName, setWlName,
  wlColor, setWlColor,
  wlLogo, setWlLogo
}) {



  // ⚠️ OS ESTADOS DE NOME, COR E LOGO FORAM APAGADOS DAQUI! 
  // Eles não ficam mais no useState local, pois já estão vindo como Props ali em cima.

  const [wlSaveText, setWlSaveText] = useState('Salvar Configurações');
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle', 'syncing', 'success'
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);

  // Intercepta o upgrade para mostrar o modal de criação de conta
  const handleUpgradeClick = (planId, planName, planPrice) => {
    // Salva o plano pendente
    setPendingPlan({ id: planId, name: planName, price: planPrice });
    // Mostra o modal de criação de conta
    setShowCreateAccount(true);
  };

  // ==========================================
  // HANDLERS AUXILIARES
  // ==========================================
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setWlLogo(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveWl = () => {
    setWlSaveText('Salvando na Nuvem...');
    setTimeout(() => setWlSaveText('Configurações Salvas!'), 1500);
    setTimeout(() => setWlSaveText('Salvar Configurações'), 4000);
  };

  // ==========================================
  // MATEMÁTICA REAL-TIME (APEX)
  // ==========================================
  // 🔥 CORRIGIDO (2026-10-03): usa o EBITDA AJUSTADO, igual ao Dashboard e ao PDF —
  // antes o APEX do app partia do EBITDA bruto e dava valuation diferente do computador.
  const _ebitdaApex = (report?.ebitdaAjustado ?? report?.ebitda);
  const baseEbitda = _ebitdaApex > 0 ? _ebitdaApex : 0;
  const deb = formData?.debt ? Number(formData.debt.replace(/\./g, '').replace(',', '.')) || 0 : 0;

  const liveEnterpriseValue = baseEbitda * apexMultiple * 12;
  const waccDiscount = 1 - ((apexWacc - 12.5) / 100);
  const liveEquityValue = Math.max(0, (liveEnterpriseValue * waccDiscount) - deb);
  const formatApexValue = (val) => {
    if (val >= 1000000000) return `R$ ${(val / 1000000000).toFixed(2).replace('.', ',')}Bi`;
    if (val >= 1000000) return `R$ ${(val / 1000000).toFixed(1).replace('.', ',')}Mi`;
    if (val >= 1000) return `R$ ${(val / 1000).toFixed(0)}K`;
    return `R$ ${val.toFixed(0)}`;
  };

  // ==========================================
  // REGRAS DE ACESSO E CONVERSÃO
  // ==========================================
  const planLevel = {
    'free': 0,
    'entry': 1,
    'tactical': 2,
    'plus': 3,
    'total': 4,
    'advisor': 5,
    'boutique': 6,
    'enterprise': 7
  };

  // 🔥 FUNÇÃO DE ACESSO CORRIGIDA - SÓ LIBERA SE O NÍVEL FOR MAIOR OU IGUAL
  const hasAccess = (requiredPlan) => {
    const userPlanKey = userPlan || 'free';
    const userLevel = planLevel[userPlanKey] || 0;
    const requiredLevel = planLevel[requiredPlan] || 0;
    return userLevel >= requiredLevel;
  };

  const getPlanName = () => {
    // 1. Definimos o dicionário de nomes corretamente
    const names = {
      'free': 'Gratuito',
      'entry': 'Entry (Visão)',
      'tactical': 'Tactical (Operação)',
      'plus': 'Plus (CFO Virtual)',
      'total': 'Total (M&A Master)',
      'boardroom': 'Boardroom',
      'advisor': 'Advisor PRO',
      'boutique': 'Boutique Elite',
      'enterprise': 'Enterprise Ultra'
    };

    // 2. Padronizamos a variável do plano para minúsculo (para evitar erros se vier maiúsculo do MobileInterface)
    const planKey = userPlan ? String(userPlan).toLowerCase() : 'free';

    // 3. Retornamos o nome bonito!
    return names[planKey] || 'Plano Corporativo';
  };

  // 🔥 STATUS RÁPIDO DE SAÚDE FINANCEIRA (leitura visual instantânea na Carteira)
  const parseMoeda = (v) => v ? Number(String(v).replace(/\./g, '').replace(',', '.')) || 0 : 0;
  const getClientHealth = (client) => {
    const fd = client?.snapshot?.formData || {};
    const revenue = parseMoeda(fd.revenue);
    const costs = parseMoeda(fd.costs);
    const debt = parseMoeda(fd.debt);
    if (!revenue) return { label: 'Sem dados', color: 'bg-slate-500', text: 'text-white' };

    const core = calculateCoreMetrics(revenue, costs, debt, fd.segment);
    const health = calculateHealthStatus({ ebitda: core.ebitdaAjustado, alavancagem: core.alavancagem, deb: debt, rev: revenue, sector: fd.segment });

    const styleMap = {
      'Falência Iminente': { color: 'bg-red-600', text: 'text-white' },
      'Crítico': { color: 'bg-red-500', text: 'text-white' },
      'Atenção': { color: 'bg-amber-400', text: 'text-black' },
      'Saudável': { color: 'bg-lime-400', text: 'text-black' }
    };
    return { label: health.label, ...styleMap[health.label] };
  };
  const consultorClients = historyList.filter(c => String(c.snapshot?.userType || c.snapshot?.formData?.userType || '').toLowerCase() === 'consultor');
  const [carteiraFiltro, setCarteiraFiltro] = useState('todos'); // todos | Saudável | Atenção | Crítico | Falência Iminente
  const consultorClientsFiltrados = carteiraFiltro === 'todos'
    ? consultorClients
    : consultorClients.filter(c => getClientHealth(c).label === carteiraFiltro);
  const isValuationUp = liveEquityValue >= (finance?.equityValue || 0);
  const apexStatus = isValuationUp ? "OTIMIZAÇÃO DE VALOR" : "ALERTA DE DESVALORIZAÇÃO";
  const apexStatusColor = isValuationUp ? "text-blue-400" : "text-red-400";
  const apexStatusBg = isValuationUp ? "bg-blue-500/5 border-blue-500/20" : "bg-red-500/5 border-red-500/20";
  const apexRationale = isValuationUp
    ? `A reprecificação do WACC para ${apexWacc}% e a expansão do múltiplo para ${apexMultiple}x indicam um cenário macroeconômico favorável. A redução do risco operacional atrai prêmios maiores em rodadas de M&A.`
    : `A elevação do WACC para ${apexWacc}% e a contração do múltiplo para ${apexMultiple}x sinalizam estresse estrutural. O aumento do risco drena o fluxo de caixa descontado da operação imediatamente.`;

  // ==========================================
  // VITRINE DE UPGRADE B2B - PREMIUM GLASSMORPHISM (SEM BLINK)
  // ==========================================
  const renderUpgradePaywall = (planId, planName, price, features) => {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 animate-fade-up font-dashboard overflow-hidden">
        <div className="bg-slate-900/40 backdrop-blur-xl border border-white/5 p-10 rounded-[3rem] w-full max-w-lg space-y-8 text-center shadow-2xl relative overflow-hidden group">

          {/* Aura Luminosa de Fundo - Estática e Isolada */}
          <div className={`absolute -top-14 -right-14 w-40 h-40 rounded-full blur-[70px] opacity-20 pointer-events-none ${planId === 'boutique' ? 'bg-lime-400' : 'bg-blue-500'}`}></div>

          <div className="flex flex-col items-center relative z-10">
            <div className="w-20 h-20 bg-[#020617] rounded-3xl flex items-center justify-center mb-6 shadow-inner border border-white/5">
              <Lock size={32} className={planId === 'boutique' ? 'text-lime-400' : 'text-blue-400'} />
            </div>

            <span className={`px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest mb-4 border border-white/10 ${planId === 'boutique' ? 'text-lime-400 bg-lime-400/5' : 'text-blue-400 bg-blue-500/5'}`}>
              Módulo Restrito • Console Partner B2B
            </span>

            <h3 className="text-4xl font-black text-white italic uppercase tracking-tighter leading-none mb-3">
              {planName}
            </h3>
            <p className="text-[11px] text-slate-400 tracking-wider leading-relaxed mb-8 max-w-sm mx-auto">
              Sua infraestrutura atual não possui permissão para este módulo. Faça o upgrade estratégico agora mesmo.
            </p>

            <div className={`px-8 py-6 rounded-[2rem] flex flex-col items-center w-full mb-8 transform transition-transform hover:scale-[1.02] ${planId === 'boutique' ? 'bg-lime-400 text-black shadow-[0_0_30px_rgba(163,230,53,0.2)]' : 'bg-blue-600 text-white shadow-[0_0_30px_rgba(59,130,246,0.2)]'}`}>
              <div className="flex items-baseline gap-1">
                <span className="font-black text-xl italic uppercase">R$</span>
                <span className="font-black text-5xl tracking-tighter italic leading-none">{price}</span>
                <span className="text-[11px] font-bold uppercase tracking-widest ml-1 opacity-80">/mês</span>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-6 text-left relative z-10">
            <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4">
              Recursos Avançados Desbloqueados:
            </p>
            <ul className="space-y-4">
              {features.map((feat, i) => (
                <li key={i} className="flex items-start gap-3 text-[11px] font-bold text-slate-300 uppercase tracking-widest leading-tight">
                  <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 shadow-[0_0_10px_currentColor] ${planId === 'boutique' ? 'bg-lime-400 text-lime-400' : 'bg-blue-400 text-blue-400'}`}></div>
                  <span>{feat}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Chamada segura que impede loops na renderização */}
          <button
            onClick={(e) => {
              e.preventDefault();
              if (typeof onUpgradeRequest === 'function') {
                onUpgradeRequest(planId, planName, price, 'ASSINATURA MENSAL');
              } else {
                setPendingPlan({ id: planId, name: planName, price });
                setShowCreateAccount(true);
              }
            }}
            className={`w-full py-6 rounded-full font-black uppercase text-xs tracking-widest shadow-xl active:scale-95 transition-all relative z-10 mt-4 flex items-center justify-center gap-2 ${planId === 'boutique' ? 'bg-white text-black' : 'bg-slate-800 text-white border border-white/10 hover:bg-slate-700'}`}
          >
            <Zap size={14} /> Solicitar Liberação do Módulo
          </button>
        </div>
      </div>
    );
  };

  const renderTooltip = (title, mobileText, webText, colorTheme) => {
    const isBlue = colorTheme === 'blue';
    const isPurple = colorTheme === 'purple';
    const textColor = isBlue ? 'text-blue-400' : isPurple ? 'text-purple-400' : 'text-lime-400';
    const borderColor = isBlue ? 'border-blue-500/20 bg-blue-500/5' : isPurple ? 'border-purple-500/20 bg-purple-500/5' : 'border-white/5 bg-slate-900/30';
    return (
      <div className={`${borderColor} border rounded-2xl p-4 flex flex-col gap-3 mb-4 text-left`}>
        <div className="flex gap-3 items-start">
          <Monitor size={16} className={`${textColor} shrink-0 mt-0.5`} />
          <p className="text-xs text-slate-400 leading-relaxed">
            <strong className={`${textColor} block mb-0.5 uppercase tracking-wide`}>{title}:</strong>
            <span className="text-white font-bold">No Celular:</span> {mobileText}
          </p>
        </div>
        <div className="flex gap-3 items-start border-t border-white/5 pt-3">
          <Monitor size={16} className={`${textColor} shrink-0 mt-0.5`} />
          <p className="text-xs text-slate-400 leading-relaxed">
            <span className="text-white font-bold">No Computador:</span> {webText}
          </p>
        </div>
      </div>
    );
  };

  return (
    // 🔥 Na aba "Agentes de IA" (teaser renderizado fora deste componente) este
    // contêiner fica vazio, mas o pb-32 + espaçamentos abriam ~180px em branco
    // acima do card. Escondido (sem desmontar, pra não perder o estado das abas).
    <div className="space-y-6 pb-32" style={reportTab === 'agentes-ia' ? { display: 'none' } : undefined}>

      {/* ========================================== */}
      {/* ABA 1: RELATÓRIO (GRATUITO)                */}
      {/* ========================================== */}
      {reportTab === 'visao' && (
        <div className="space-y-6 animate-fade-up">
          {renderTooltip(
            "Ecossistema Phoenix OS",
            "Gere o Dossiê em PDF e impressione o cliente instantaneamente na mesa de reunião.",
            "O Painel Web libera o CFO Virtual em tela gigante, além do Simulador Avançado (Antes×Depois, sensibilidade combinada) e do Boardroom M&A (estrutura de capital, checklist de prontidão, benchmark de setor) — recursos exclusivos do Desktop.",
            "lime"
          )}

          <div className={`${report.status.bg} ${report.status.border} p-6 rounded-[2.5rem] border flex items-center gap-5 shadow-[0_0_30px_rgba(190,242,100,0.1)]`}>
            <div className="p-3 rounded-full bg-black/20 drop-shadow-[0_0_30px_currentColor]">
              {report.status.icon}
            </div>
            <div>
              <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Status da Operação</p>
              <p className={`${report.status.color} text-3xl font-black uppercase italic tracking-tighter drop-shadow-[0_0_10px_currentColor]`}>
                {report.status.label}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 shadow-lg flex flex-col justify-center overflow-hidden">
              <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">EBITDA Real Time</p>
              <p className="font-black text-white italic tracking-tighter whitespace-nowrap" style={{ fontSize: 'clamp(1rem, 5.5vw, 1.5rem)' }}>R$ {formatCurrency(report.ebitda)}</p>
            </div>

            <div className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 shadow-lg flex flex-col justify-center overflow-hidden">
              <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Valor da operação (Enterprise Value)</p>
              <p className="font-black text-white italic tracking-tighter whitespace-nowrap" style={{ fontSize: 'clamp(1rem, 5.5vw, 1.5rem)' }}>R$ {formatCurrency(finance.enterpriseValue || 0)}</p>
              <p className="text-[11px] text-slate-500 leading-snug mt-1.5">EBITDA anual × múltiplo do setor, antes de descontar a dívida.</p>
            </div>

            <div className="bg-gradient-to-br from-lime-400/10 to-transparent p-6 rounded-[2rem] border border-lime-400/30 shadow-lg flex flex-col justify-center overflow-hidden">
              <p className="text-[11px] font-black text-lime-400 uppercase tracking-widest mb-1">Valuation (Equity Value)</p>
              <p className="font-black text-lime-400 italic tracking-tighter whitespace-nowrap" style={{ fontSize: 'clamp(1rem, 6vw, 1.875rem)' }}>R$ {formatCurrency(finance.equityValue)}</p>
              <p className="text-[11px] text-lime-200/70 leading-snug mt-1.5">Valor da operação menos a dívida: o que sobra para os sócios.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 shadow-lg">
                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Alavancagem</p>
                <p className="text-xl font-black text-white italic tracking-tighter truncate">{String(report.alavancagem).replace('.', ',')}x</p>
              </div>
              <div className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 shadow-lg">
                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Margem</p>
                <p className="text-xl font-black text-white italic tracking-tighter truncate">{String(report.currentMargin).replace('.', ',')}%</p>
              </div>
            </div>
          </div>

          {/* 🔥 PARIDADE COM O GESTOR: custo, dívida, ponto de equilíbrio e
              caixa mínimo — informações básicas que existiam só no lado
              Gestor. O Consultor também precisa disso pra entender a saúde
              financeira do cliente rapidamente, sem precisar abrir o APEX
              (que é focado em valuation, não no dia a dia financeiro). */}
          {(() => {
            const parseValorBR = (v) => {
              if (!v) return 0;
              if (typeof v === 'number') return v;
              return Number(v.toString().replace(/\./g, '').replace(',', '.')) || 0;
            };
            const custNum = parseValorBR(formData.costs);
            const debNum = parseValorBR(formData.debt);
            const revNum = parseValorBR(formData.revenue);
            const proLaboreNum = parseValorBR(formData.proLabore);
            const favoravel = revNum >= custNum;
            return (
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 border-l-4 border-l-amber-500/60 shadow-lg flex items-center gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                    <DollarSign size={20} className="text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Custo Operacional</p>
                    <p className="text-xl font-black text-white italic tracking-tighter break-all">R$ {formatCurrency(custNum)}</p>
                  </div>
                </div>
                <div className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 border-l-4 border-l-red-500/60 shadow-lg flex items-center gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-2xl bg-red-500/10 flex items-center justify-center">
                    <CreditCard size={20} className="text-red-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Dívida Total</p>
                    <p className="text-xl font-black text-white italic tracking-tighter break-all">R$ {formatCurrency(debNum)}</p>
                  </div>
                </div>
                <div className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 border-l-4 border-l-blue-500/60 shadow-lg flex items-center gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                    <Scale size={20} className="text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Ponto de Equilíbrio</p>
                    <p className="text-xl font-black text-white italic tracking-tighter break-all">R$ {formatCurrency(custNum)}</p>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {favoravel ? `R$ ${formatCurrency(Math.abs(revNum - custNum))} acima` : `R$ ${formatCurrency(Math.abs(revNum - custNum))} abaixo`}
                    </p>
                  </div>
                </div>
                <div className="bg-slate-900/40 p-6 rounded-[2rem] border border-white/5 border-l-4 border-l-lime-500/60 shadow-lg flex items-center gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-2xl bg-lime-500/10 flex items-center justify-center">
                    <Wallet size={20} className="text-lime-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-1">Caixa Mínimo Mensal</p>
                    <p className="text-xl font-black text-white italic tracking-tighter break-all">R$ {formatCurrency(custNum + proLaboreNum)}</p>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="bg-slate-950/40 p-8 rounded-[3rem] border border-white/5 shadow-inner mt-6">
            <div className="flex items-center justify-between flex-wrap gap-y-3 border-b border-white/5 pb-4 mb-8">
              <div className="flex items-center gap-3 shrink-0">
                <Activity className="text-lime-400 drop-shadow-[0_0_5px_#bef264]" size={20} />
                <h3 className="text-[11px] font-black text-white uppercase tracking-widest italic leading-none whitespace-nowrap">Parecer Técnico</h3>
              </div>
              <span className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest border flex items-center justify-center gap-1.5 whitespace-nowrap shrink-0 ${report.status.bg} ${report.status.color} ${report.status.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${report.status.color.replace('text-', 'bg-')}`}></span>
                {report.status.label}
              </span>
            </div>
            <div className="space-y-10 text-left">
              {(carregandoParecer || iaGerando) ? (
                <div className="space-y-3 animate-pulse">
                  <div className="h-3 bg-white/5 rounded-full w-3/4" />
                  <div className="h-3 bg-white/5 rounded-full w-full" />
                  <div className="h-3 bg-white/5 rounded-full w-5/6" />
                </div>
              ) : parecerIATexto ? (
                <div className="space-y-1 text-left">
                  {typeof formatarParecerIA === 'function' ? formatarParecerIA(parecerIATexto) : parecerIATexto}
                  <p className="text-[11px] text-slate-600 uppercase tracking-widest font-bold mt-4 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-lime-400" /> Gerado por IA
                  </p>
                </div>
              ) : (
                /* 🔥 CORRIGIDO: aqui caía no texto ESTÁTICO (dynamicInsights)
                   enquanto a IA não chegava — o cliente lia um parecer
                   genérico, cheio de "sugere que", que depois era trocado
                   por um texto completamente diferente. Pior que esperar:
                   dá a impressão de que a análise mudou de opinião sozinha.
                   Agora avisa que está gerando, igual a tela do Gestor
                   (MobileInterface) já fazia. */
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-lime-400 border-t-transparent rounded-full animate-spin shrink-0" />
                  <p className="text-[11px] text-slate-500 italic">Gerando o parecer com IA... salve o diagnóstico se ainda não salvou.</p>
                </div>
              )}
            </div>
          </div>

          {/* 🔮 PROJEÇÃO POR IA ESTOCÁSTICA — sempre mostra os 3 grupos
              (Curto/Médio/Longo = 3, 6, 12, 24 e 36 meses), igual ao
              Dashboard Consultor. Gating: Advisor PRO → 3 e 6 meses.
              Boutique Elite → +12. Enterprise Ultra → +24 e 36. O que não
              tiver liberado continua visível, com cadeado, sempre
              clicável (abre o checkout). */}
          {(() => {
            const parseValorBR = (v) => {
              if (!v) return 0;
              if (typeof v === 'number') return v;
              return Number(v.toString().replace(/\./g, '').replace(',', '.')) || 0;
            };
            const projecaoCompleta = calculateStochasticProjection(
              { ebitda: report.ebitda, ebitdaAjustado: report.ebitdaAjustado, currentMargin: Number(report.currentMargin) },
              parseValorBR(formData.revenue)
            );
            const grupos = [
              { chave: 'curto', titulo: 'Curto Prazo', meses: [3, 6], liberado: hasAccess('advisor'), texto: leituraProjecaoCurto, planoNecessario: 'advisor', nomePlano: 'Advisor PRO' },
              { chave: 'medio', titulo: 'Médio Prazo', meses: [12], liberado: hasAccess('boutique'), texto: leituraProjecaoMedio, planoNecessario: 'boutique', nomePlano: 'Boutique Elite' },
              { chave: 'longo', titulo: 'Longo Prazo', meses: [24, 36], liberado: hasAccess('enterprise'), texto: leituraProjecaoLongo, planoNecessario: 'enterprise', nomePlano: 'Enterprise Ultra' },
            ];
            return (
              <div className="bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-500/20 px-5 py-8 rounded-[3rem] mt-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10"><Wand size={70} className="text-purple-400" /></div>
                <div className="flex items-center gap-2 mb-1 relative z-10">
                  <Wand size={16} className="text-purple-400" />
                  <p className="text-sm font-black text-purple-300 uppercase tracking-widest italic">Pra onde esse negócio está indo?</p>
                </div>
                <p className="text-[11px] text-slate-500 mb-5 relative z-10">Não é só um retrato de hoje — é pra onde a empresa está indo, em 3 cenários.</p>
                <div className="space-y-4 relative z-10">
                  {grupos.map((grupo) => {
                    if (!grupo.liberado) {
                      return (
                        <button
                          key={grupo.chave}
                          onClick={() => typeof onUpgradeRequest === 'function' && onUpgradeRequest(grupo.planoNecessario)}
                          className="w-full flex items-center justify-between bg-black/20 border border-white/5 rounded-2xl px-4 py-3.5 hover:border-purple-500/30 transition-all active:scale-[0.99]"
                        >
                          <p className="text-[11px] font-black text-slate-600 uppercase tracking-widest flex items-center gap-1.5">
                            <Lock size={10} /> {grupo.titulo} ({grupo.meses.join(' e ')} meses)
                          </p>
                          <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest">Desbloquear no {grupo.nomePlano}</p>
                        </button>
                      );
                    }
                    return (
                      <div key={grupo.chave} className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-4">
                        <p className="text-[11px] font-black text-purple-300 uppercase tracking-widest mb-2.5">{grupo.titulo} ({grupo.meses.join(' e ')} meses)</p>
                        <div className="space-y-3">
                          {grupo.meses.map((h) => {
                            const dado = projecaoCompleta.find(p => p.months === h);
                            if (!dado) return null;
                            return (
                              <div key={h} className="bg-black/20 rounded-xl p-3">
                                <p className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2.5 text-center">{h} meses</p>
                                {/* 🔥 CORRIGIDO: 3 colunas não comportam valores em
                                    bilhões — os números se sobrepunham. Empilhado. */}
                                <div className="space-y-1.5">
                                  <div className="flex items-center justify-between gap-3">
                                    <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide shrink-0">Pessimista</p>
                                    <p className="text-[11px] font-black text-red-300 text-right">R$ {formatCurrency(dado.pessimista)}</p>
                                  </div>
                                  <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-1.5">
                                    <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide shrink-0">Provável</p>
                                    <p className="text-[11px] font-black text-white text-right">R$ {formatCurrency(dado.provavel)}</p>
                                  </div>
                                  <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-1.5">
                                    <p className="text-[11px] text-slate-500 uppercase font-bold tracking-wide shrink-0">Otimista</p>
                                    <p className="text-[11px] font-black text-lime-300 text-right">R$ {formatCurrency(dado.otimista)}</p>
                                  </div>
                                </div>
                                {!grupo.texto && (
                                  <p className="text-[11px] text-slate-400 leading-relaxed border-t border-white/5 pt-2.5 mt-2.5">
                                    {interpretarHorizonte(dado, h)}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {/* 🔮 LEITURA DA PROJEÇÃO desse grupo — gerada pela IA
                            em tom de futuro/previsão, nunca repetindo o
                            presente. Enquanto não chegou, mostra carregando. */}
                        {grupo.texto ? (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            {formatarParecerIA(grupo.texto)}
                          </div>
                        ) : hasAccess(grupo.planoNecessario) && (carregandoParecer || iaGerando) ? (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10">
                            <div className="w-2.5 h-2.5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin shrink-0" />
                            <p className="text-[11px] text-slate-500 italic">Gerando a análise desse prazo com IA...</p>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                <p className="text-[11px] text-slate-600 uppercase tracking-widest font-bold mt-5 flex items-center gap-1.5 relative z-10">
                  <span className="w-1 h-1 rounded-full bg-purple-400" /> Previsão gerada por IA
                </p>
              </div>
            );
          })()}

          {/* 🏆 OPORTUNIDADES ESTRATÉGICAS — card separado, exclusivo do
              Enterprise Ultra. Justifica visualmente o salto de preço em
              relação ao Boutique: vai além de diagnóstico/projeção. */}
          {hasAccess('enterprise') && oportunidadesTexto && (
            <div className="bg-gradient-to-br from-amber-900/20 via-amber-950/10 to-transparent border border-amber-500/30 p-8 rounded-[3rem] mt-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Crown size={70} className="text-amber-400" /></div>
              <div className="flex items-center gap-2 mb-1 relative z-10">
                <Crown size={16} className="text-amber-400" />
                <p className="text-sm font-black text-amber-300 uppercase tracking-widest italic">Oportunidades Estratégicas</p>
              </div>
              <p className="text-[11px] text-amber-500/70 uppercase tracking-widest font-black mb-5 relative z-10">Exclusivo Enterprise Ultra</p>
              <div className="relative z-10">
                {formatarParecerIA(oportunidadesTexto)}
              </div>
            </div>
          )}

          {/* BOTÃO 1: SINCRONIZAR COM O DASHBOARD WEB REAL-TIME */}
          <button
            disabled={syncStatus === 'syncing'}
            onClick={async () => {
              if (!onSyncDashboard) return;
              setSyncStatus('syncing');
              const success = await onSyncDashboard();
              if (success) {
                setSyncStatus('success');
                setTimeout(() => setSyncStatus('idle'), 3000);
              } else {
                setSyncStatus('idle');
                alert("Erro ao conectar com o servidor Firebase.");
              }
            }}
            className={`w-full py-6 rounded-full font-black uppercase text-xs tracking-widest flex items-center justify-center gap-3 transition-all active:scale-95 border mt-6 ${syncStatus === 'success'
              ? 'bg-lime-500/10 text-lime-400 border-lime-400/40 shadow-[0_0_15px_rgba(163,230,53,0.2)]'
              : 'bg-blue-600 hover:bg-blue-500 text-white border-transparent shadow-[0_0_20px_rgba(59,130,246,0.3)]'
              }`}
          >
            {syncStatus === 'idle' && (
              <>
                <Zap size={18} className="animate-pulse" /> Sincronizar com Console Web
              </>
            )}
            {syncStatus === 'syncing' && (
              <>
                <Loader2 size={18} className="animate-spin" /> Transmitindo Dados...
              </>
            )}
            {syncStatus === 'success' && (
              <>
                <CheckCircle2 size={18} /> Enviado para o Computador!
              </>
            )}
          </button>

          {/* BOTÃO 2: EXPORTAR DOSSIÊ PADRÃO */}
          <button onClick={() => setShowDownloadConfirm(true)} className="w-full bg-white text-black py-6 rounded-full font-black uppercase text-xs tracking-widest shadow-[0_0_20px_rgba(255,255,255,0.2)] flex items-center justify-center gap-3 hover:scale-[1.02] active:scale-95 transition-all mt-4">
            <Download size={18} /> Exportar Dossiê Master
          </button>

          {/* 🔥 BOTÃO 3: EDITAR DADOS DO RELATÓRIO */}
          <button
            onClick={() => typeof onEditClient === 'function' && onEditClient()}
            className="w-full bg-slate-800 hover:bg-slate-700 border border-white/10 text-white py-5 rounded-full font-black uppercase text-[11px] tracking-widest shadow-inner flex items-center justify-center gap-3 transition-all active:scale-95 mt-4"
          >
            <Settings size={16} className="text-slate-400" /> Editar Dados do Relatório
          </button>

          {!hasAccess('advisor') && renderUpgradePaywall('advisor', 'Advisor PRO', '499,00', ['Gestão de até 5 CNPJs simultâneos', 'Módulo APEX (Valuation ao vivo, múltiplo e WACC ajustáveis)', 'IA Estocástica — Projeção de 3 e 6 Meses', 'Mesa de Operações e CRM de Carteira', 'Simulações e Diagnósticos Ilimitados por IA', 'Exportação de Dossiês Ilimitada', 'Dossiês salvos na nuvem, acesso de qualquer aparelho'])}
        </div>
      )}

      {/* ========================================== */}
      {/* ABA 2: CARTEIRA - ADVISOR PRO              */}
      {/* ========================================== */}
      {reportTab === 'carteira' && (
        <div className="space-y-6 animate-fade-up">
          {renderTooltip(
            "CRM Executivo",
            "Acesso rápido aos dados vitais dos clientes para responder dúvidas de imediato.",
            "Painel Web em estilo Kanban avançado para gerenciar a jornada de M&A de cada CNPJ.",
            "lime"
          )}

          <div className="flex flex-wrap justify-between items-center gap-3 mt-2">
            <h2 className="text-xl font-black text-white uppercase italic tracking-tighter min-w-0">
              Meus <span className="text-lime-400 drop-shadow-[0_0_8px_rgba(190,242,100,0.5)]">Relatórios</span>
            </h2>
            <button
              onClick={onNewClient}
              className={`shrink-0 px-4 py-3 rounded-xl text-[11px] font-black uppercase flex items-center gap-2 shadow-[0_0_15px_rgba(190,242,100,0.2)] whitespace-nowrap ${hasAccess('advisor') ? 'bg-lime-400 text-black hover:bg-lime-500' : 'bg-slate-800 text-slate-500 cursor-not-allowed'}`}
              disabled={!hasAccess('advisor')}
            >
              <Plus size={14} className="shrink-0" /> Novo Cliente
            </button>
          </div>

          {hasAccess('advisor') ? (
            <div className="space-y-4">
              {/* FILTRO POR STATUS DE SAÚDE */}
              {consultorClients.length > 0 && (
                <div className="flex flex-nowrap gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                  {[
                    { key: 'todos', label: 'Todos', color: 'bg-slate-700 text-white' },
                    { key: 'Saudável', label: 'Saudável', color: 'bg-lime-400 text-black' },
                    { key: 'Atenção', label: 'Atenção', color: 'bg-amber-400 text-black' },
                    { key: 'Crítico', label: 'Crítico', color: 'bg-red-500 text-white' },
                    { key: 'Falência Iminente', label: 'Falência', color: 'bg-red-600 text-white' },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setCarteiraFiltro(f.key)}
                      className={`shrink-0 whitespace-nowrap px-2.5 py-1.5 rounded-full text-[11px] font-black uppercase tracking-normal border transition-all ${carteiraFiltro === f.key ? `${f.color} border-transparent` : 'bg-slate-900/40 text-slate-400 border-white/10 hover:border-white/30'
                        }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}

              {consultorClients.length === 0 ? (
                <div className="bg-slate-900/40 border border-white/5 rounded-3xl p-10 text-center">
                  <Briefcase size={32} className="mx-auto text-slate-600 mb-3" />
                  <p className="text-sm text-slate-400 font-bold uppercase tracking-widest">Sua carteira está vazia.</p>
                  <p className="text-[11px] text-slate-500 mt-2 mb-6">Dossiês do perfil 'Gestor' não aparecem aqui.</p>
                  <button
                    onClick={onNewClient}
                    className="bg-lime-400 text-black px-6 py-3 rounded-xl text-[11px] font-black uppercase flex items-center gap-2 mx-auto hover:bg-lime-500 active:scale-95 transition-all"
                  >
                    <Plus size={14} /> Cadastrar Primeiro Cliente
                  </button>
                </div>
              ) : consultorClientsFiltrados.length === 0 ? (
                <div className="bg-slate-900/40 border border-white/5 rounded-3xl p-8 text-center">
                  <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest">Nenhum cliente com status "{carteiraFiltro}".</p>
                </div>
              ) : (
                consultorClientsFiltrados.map((client) => {
                  const health = getClientHealth(client);
                  return (
                    // 🔥 REESTRUTURADO: era um layout de 2 colunas lado a lado (nome à
                    // esquerda dividindo espaço horizontal com os botões à direita),
                    // por isso o nome truncava bem antes do fim do card mesmo sobrando
                    // espaço visual. Agora é uma coluna só: nome em linha própria usando
                    // a largura inteira do card (reticências só quando o nome for
                    // realmente maior que isso), e uma linha de baixo com badge/data à
                    // esquerda e os botões de ação à direita — mesmo padrão já usado no
                    // card de dossiê do Gestor.
                    <div key={client.id} className="bg-slate-900/40 border border-white/5 rounded-3xl p-6 hover:border-lime-400/30 transition-all group shadow-lg">
                      <div className="flex items-center gap-2 min-w-0 cursor-pointer" onClick={() => loadDossier(client)}>
                        <span className={`w-2 h-2 rounded-full ${health.color} shrink-0`} title={health.label}></span>
                        <h3 className="font-black text-white text-base group-hover:text-lime-400 transition-colors uppercase tracking-tighter truncate min-w-0">{client.name}</h3>
                      </div>
                      {/* Selos (plano + saúde) em linha própria, com a largura toda do card — antes dividiam a linha com os botões e
                          o selo "FALÊNCIA IMINENTE" quebrava para a linha de baixo. */}
                      <div className="pl-4 mt-3 flex items-center gap-2 flex-wrap min-w-0 cursor-pointer" onClick={() => loadDossier(client)}>
                        <span className="text-[11px] px-2 py-0.5 rounded-md bg-slate-800 text-slate-300 font-black uppercase border border-white/10 shrink-0">
                          {client.plan}
                        </span>
                        <span className={`text-[11px] px-1.5 py-0.5 rounded-md font-black uppercase ${health.text} leading-tight whitespace-nowrap shrink-0 ${health.color}`}>
                          {health.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-2">
                        <span className="pl-4 block text-[11px] text-slate-400 font-bold tracking-widest cursor-pointer" onClick={() => loadDossier(client)}>{client.date}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          {typeof setItemToDelete === 'function' && (
                            <button onClick={(e) => { e.stopPropagation(); setItemToDelete(client.id); }} className="w-9 h-9 shrink-0 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors active:scale-95"><Trash2 size={14} /></button>
                          )}
                          <div onClick={() => loadDossier(client)} className="w-10 h-10 shrink-0 rounded-full bg-lime-400/10 flex items-center justify-center group-hover:bg-lime-400/20 transition-colors cursor-pointer">
                            <ChevronRight size={18} className="text-lime-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            renderUpgradePaywall('advisor', 'Advisor PRO', '499,00', ['Gestão de até 5 CNPJs simultâneos', 'Módulo APEX (Valuation ao vivo, múltiplo e WACC ajustáveis)', 'IA Estocástica — Projeção de 3 e 6 Meses', 'Mesa de Operações e CRM de Carteira', 'Simulações e Diagnósticos Ilimitados por IA', 'Exportação de Dossiês Ilimitada', 'Dossiês salvos na nuvem, acesso de qualquer aparelho'])
          )}
        </div>
      )}

      {/* ========================================== */}
      {/* ABA: APEX (VALUATION & DCF - ESCADA PREMIUM) */}
      {/* ========================================== */}
      {reportTab === 'apex' && (
        <div className="w-full animate-fade-up space-y-4">

          {/* CAIXA DE DICAS */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-4 flex flex-col gap-3 text-left">
            <div className="flex gap-3 items-start">
              <Wand size={16} className="text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-400 leading-relaxed">
                <strong className="text-white block mb-0.5 uppercase tracking-wide">Módulo Valuation Builder:</strong>
                Ajuste os múltiplos de mercado e taxas para precificar o ativo e gerar as projeções de perpetuidade síncronas.
              </p>
            </div>
            <div className="flex gap-3 items-start border-t border-blue-500/10 pt-3">
              <Monitor size={16} className="text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-400 leading-relaxed">
                <strong className="text-blue-400 block mb-0.5 uppercase tracking-wide">Ambiente do Ecossistema:</strong>
                O <strong>Celular</strong> opera como coletor e simulador rápido de Valuation de campo. Para auditoria profunda forense B2B, acesse o Phoenix OS pelo seu <strong>Computador (Desktop)</strong>.
              </p>
            </div>
          </div>

          {/* ============================================================ */}
          {/* CONTROLE DE ACESSO - NÍVEL 1: ADVISOR PRO (R$ 499)          */}
          {/* ============================================================ */}
          {!hasAccess('advisor') ? (
            /* 🔒 SEM PLANO: Paywall Advisor PRO (R$ 499) */
            <div className="w-full bg-gradient-to-b from-slate-900/60 to-slate-950/40 backdrop-blur-3xl border border-white/10 p-8 md:p-12 rounded-[2.5rem] text-center shadow-[0_24px_60px_-15px_rgba(0,0,0,0.7)] relative overflow-hidden group">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-600/10 rounded-full blur-[120px] pointer-events-none group-hover:scale-110 transition-transform duration-1000"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20 mx-auto mb-6 shadow-inner">
                  <TrendingUp size={28} className="text-blue-400" />
                </div>
                <span className="text-[11px] font-black text-blue-400 uppercase tracking-widest block mb-2">Primeiro Degrau</span>
                <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter mb-4">Plano <span className="text-blue-400">Advisor PRO</span></h3>
                <div className="flex flex-row items-baseline justify-center gap-0.5 my-8 whitespace-nowrap">
                  <span className="text-xl font-black text-blue-400 italic mr-1">R$</span>
                  <span className="text-6xl font-black text-white tracking-tighter italic leading-none">499</span>
                  <span className="text-xl font-black text-white italic">,00</span>
                  <span className="text-[11px] text-slate-500 font-black uppercase ml-2">/ mês</span>
                </div>
                <p className="text-[11px] text-slate-400 font-bold tracking-normal mb-6 max-w-sm mx-auto leading-relaxed">
                  Desbloqueie o motor de Valuation em tempo real com ajuste de múltiplos e WACC. Simule cenários de M&A com seus clientes instantaneamente.
                </p>
                <div className="flex flex-col gap-2 mb-6 text-left max-w-sm mx-auto">
                  {[
                    '📊 Sliders Múltiplo & WACC ao vivo',
                    '⚡ Valuation em Tempo Real',
                    '🔭 IA Estocástica — Projeção de 3 e 6 Meses',
                    '👥 Carteira de até 5 CNPJs simultâneos',
                    '🗂️ Mesa de Operações e CRM de Carteira',
                    '🧪 Simulações e Diagnósticos Ilimitados por IA',
                    '📄 Exportação de Dossiê APEX',
                    '☁️ Dossiês salvos na nuvem, de qualquer aparelho'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 bg-blue-500/5 border border-blue-500/10 rounded-xl px-3 py-2.5">
                      <span className="text-sm leading-none mt-0.5">{item.split(' ')[0]}</span>
                      <span className="text-[11px] font-bold text-slate-300 uppercase tracking-tight leading-tight">{item.split(' ').slice(1).join(' ')}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => typeof onUpgradeRequest === 'function' && onUpgradeRequest('advisor')}
                  className="w-full py-4 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-black uppercase tracking-widest shadow-[0_0_30px_rgba(37,99,235,0.4)] transition-all active:scale-95 flex items-center justify-center gap-2 mx-auto"
                >
                  Assinar Plano <Zap size={14} className="fill-white" />
                </button>
              </div>
            </div>
          ) : !hasAccess('boutique') ? (
            /* ============================================================ */
            /* NÍVEL 2: ADVISOR PRO - Conteúdo liberado + Paywall Boutique   */
            /* ============================================================ */
            <div className="space-y-4">
              {/* Valuation */}
              <div className="bg-black/60 border border-blue-500/20 rounded-2xl py-6 px-4 flex flex-col items-center text-center shadow-inner">
                <span className="text-xs text-blue-400 font-black uppercase tracking-widest mb-2">Valuation Real-Time Projetado</span>
                <div className="flex items-baseline gap-1 justify-center flex-nowrap max-w-full px-1 overflow-hidden">
                  <span className="text-sm text-white/50 font-bold font-mono shrink-0">R$</span>
                  <span
                    className="font-black text-white tracking-tight italic whitespace-nowrap"
                    style={{ fontSize: 'clamp(1rem, 5.2vw, 2.25rem)' }}
                  >
                    {liveEquityValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {/* SLIDER MÚLTIPLO com referência do setor */}
              <ApexSlider
                label="Múltiplo de EBITDA Setorial"
                value={apexMultiple} unit="x" min={3} max={15} step={0.1}
                onChange={setApexMultiple}
                reference={finance?.multiple || 5.5}
                referenceLabel="Referência do Setor"
              />

              {/* SLIDER WACC com referência neutra (12,5% = ponto sem prêmio/desconto) */}
              <ApexSlider
                label="Taxa Cost of Capital / WACC"
                value={apexWacc} unit="%" min={8} max={25} step={0.5}
                onChange={setApexWacc}
                reference={12.5}
                referenceLabel="WACC Neutro (base)"
              />

              {/* 🔒 PAYWALL BOUTIQUE - Matriz de Sensibilidade bloqueada */}
              <div className="flex items-center gap-3 mt-2 mb-2">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent to-blue-500/30"></div>
                <span className="text-[11px] font-black text-blue-400 uppercase tracking-widest px-2">Próximo Degrau · Boutique Elite · R$ 1.997/mês</span>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent to-blue-500/30"></div>
              </div>
              <div className="bg-gradient-to-b from-slate-900/60 to-slate-950/40 border border-blue-500/20 p-6 rounded-2xl text-center">
                <Lock size={24} className="text-blue-400 mx-auto mb-3" />
                <p className="text-[11px] text-blue-400/60 uppercase tracking-widest mb-1">Mais Vantagens</p>
                <p className="text-sm font-black text-blue-400 uppercase tracking-widest mb-4">Boutique Elite</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-4">9 cenários simultâneos de valuation</p>
                <div className="bg-gradient-to-br from-blue-900/20 to-transparent border border-blue-500/20 rounded-2xl p-4 mb-5 flex items-baseline justify-center gap-1">
                  <span className="text-lg font-black text-blue-400 italic">R$</span>
                  <span className="text-6xl font-black text-white tracking-tighter italic">1.997</span>
                  <span className="text-base font-black text-white italic">,00</span>
                  <span className="text-[11px] text-slate-500 font-black uppercase ml-1">/ mês</span>
                </div>
                <p className="text-[11px] font-black uppercase tracking-widest text-blue-400 text-left mb-3">Tudo do Advisor PRO, mais:</p>
                <div className="flex flex-col gap-2 mb-5 text-left">
                  {[
                    '📊 Matriz de Sensibilidade com 9 cenários ao vivo',
                    '📈 Análise comparativa de múltiplos setoriais',
                    '🔭 IA Estocástica — Projeção +12 Meses',
                    '👥 Carteira expandida até 15 CNPJs',
                    '📄 Super Relatório 360º em PDF Ilimitado',
                    '🎯 Prioridade de suporte sobre o Advisor PRO'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 bg-blue-500/5 border border-blue-500/10 rounded-xl px-3 py-2.5">
                      <span className="text-sm leading-none mt-0.5">{item.split(' ')[0]}</span>
                      <span className="text-[11px] font-bold text-slate-300 uppercase tracking-tight leading-tight">{item.split(' ').slice(1).join(' ')}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => typeof onUpgradeRequest === 'function' && onUpgradeRequest('boutique')}
                  className="px-8 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-black uppercase tracking-widest transition-all active:scale-95">
                  Fazer Upgrade Boutique
                </button>
              </div>
            </div>
          ) : (
            /* ============================================================ */
            /* NÍVEL 3+: BOUTIQUE+ - Matriz liberada + Paywall/IA           */
            /* ============================================================ */
            <div className="space-y-4">
              {/* Valuation */}
              <div className="bg-black/60 border border-blue-500/20 rounded-2xl py-6 px-4 flex flex-col items-center text-center shadow-inner">
                <span className="text-xs text-blue-400 font-black uppercase tracking-widest mb-2">Valuation Real-Time Projetado</span>
                <div className="flex items-baseline gap-1 justify-center flex-nowrap max-w-full px-1 overflow-hidden">
                  <span className="text-sm text-white/50 font-bold font-mono shrink-0">R$</span>
                  <span
                    className="font-black text-white tracking-tight italic whitespace-nowrap"
                    style={{ fontSize: 'clamp(1rem, 5.2vw, 2.25rem)' }}
                  >
                    {liveEquityValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {/* SLIDER MÚLTIPLO com referência do setor */}
              <ApexSlider
                label="Múltiplo de EBITDA Setorial"
                value={apexMultiple} unit="x" min={3} max={15} step={0.1}
                onChange={setApexMultiple}
                reference={finance?.multiple || 5.5}
                referenceLabel="Referência do Setor"
              />

              {/* SLIDER WACC com referência neutra (12,5% = ponto sem prêmio/desconto) */}
              <ApexSlider
                label="Taxa Cost of Capital / WACC"
                value={apexWacc} unit="%" min={8} max={25} step={0.5}
                onChange={setApexWacc}
                reference={12.5}
                referenceLabel="WACC Neutro (base)"
              />

              {/* ============================================================ */}
              {/* MATRIZ DE SENSIBILIDADE - BOUTIQUE+ (UNIFICADA)             */}
              {/* ============================================================ */}
              <div className="bg-black/40 border border-blue-500/10 rounded-2xl p-5">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <p className="text-xs font-black text-blue-400 uppercase tracking-widest">Matriz de Sensibilidade (9 Cenários)</p>
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-800/50 px-2 py-1 rounded shrink-0">Equity Value (R$)</span>
                </div>

                {/* LEGENDA */}
                <div className="flex flex-wrap items-center gap-1.5 mb-4 text-[11px] leading-relaxed text-slate-400 bg-slate-800/30 rounded-lg px-3 py-2">
                  <span>📊 <strong className="text-white">Como ler:</strong> linhas = <span className="text-blue-400 font-bold">Múltiplo</span>, colunas = <span className="text-purple-400 font-bold">WACC</span>. O cruzamento é o valuation daquele cenário.</span>
                </div>

                {/* MATRIZ 3x3 EM FORMATO TABELA (linha = Múltiplo, coluna = WACC) */}
                <div className="grid grid-cols-4 gap-1.5 text-center">
                  {/* canto vazio + cabeçalho de colunas (WACC) */}
                  <div />
                  {[apexWacc - 2, apexWacc, apexWacc + 2].map((wacc) => (
                    <div key={`head-${wacc}`} className="text-[11px] font-black text-purple-300 pb-1">
                      {wacc}%
                    </div>
                  ))}

                  {/* linhas: cabeçalho (Múltiplo) + 3 valores */}
                  {[apexMultiple - 1, apexMultiple, apexMultiple + 1].map((mult) => (
                    <React.Fragment key={`row-${mult}`}>
                      <div className="text-[11px] font-black text-blue-300 flex items-center justify-center">
                        {mult.toFixed(1)}x
                      </div>
                      {[apexWacc - 2, apexWacc, apexWacc + 2].map((wacc) => {
                        const val = Math.max(0, (baseEbitda * mult * 12) * (1 - (wacc - 12.5) / 100) - deb);
                        const isCenter = mult === apexMultiple && wacc === apexWacc;
                        const isZero = val === 0;

                        let formatted;
                        if (val >= 1000000000) formatted = `${(val / 1000000000).toFixed(2).replace('.', ',')}Bi`;
                        else if (val >= 1000000) formatted = `${(val / 1000000).toFixed(2).replace('.', ',')}Mi`;
                        else if (val >= 1000) formatted = `${(val / 1000).toFixed(0)}K`;
                        else formatted = `${val.toFixed(0)}`;

                        return (
                          <div key={`${mult}-${wacc}`} className={`rounded-lg py-3 px-1 ${isCenter ? 'bg-blue-500/20 border border-blue-500/40' : isZero ? 'bg-red-500/10 border border-red-500/30' : 'bg-black/30 border border-white/5'}`}>
                            {isZero ? (
                              <span className="text-xs font-black text-red-400/70">⚠️ R$0</span>
                            ) : (
                              <span className={`text-xs font-black ${isCenter ? 'text-blue-400' : 'text-white'}`}>
                                R$ {formatted}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>

                {/* LEGENDA DE CORES DA MATRIZ */}
                <div className="flex flex-wrap items-center gap-4 mt-3 text-[11px] text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-blue-500/20 border border-blue-500/40 inline-block"></span>
                    Cenário atual
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-black/30 border border-white/5 inline-block"></span>
                    Cenário alternativo
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded bg-red-500/10 border border-red-500/30 inline-block"></span>
                    Valuation zerado (insolvência)
                  </span>
                </div>

                {/* ⚠️ AVISO DE VALUATION ZERADO */}
                {liveEquityValue === 0 && (
                  <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-red-300/80 leading-relaxed">
                      <strong className="text-red-400">⚠️ Atenção:</strong> O valuation está zerado porque o valor da dívida ({formatCurrency(deb)})
                      é igual ou superior ao Enterprise Value ({formatApexValue(liveEnterpriseValue)}).
                      Isso indica que a empresa está <strong className="text-red-400">tecnicamente insolvente</strong> —
                      o passivo oneroso consome todo o patrimônio. Recomenda-se reestruturação de capital imediata.
                    </p>
                  </div>
                )}

                {/* 🔮 LEITURA DA MATRIZ DE SENSIBILIDADE — calculada na hora,
                    sem chamada de IA, mesmo princípio do interpretarHorizonte. */}
                {(() => {
                  const valoresMatriz = [apexMultiple - 1, apexMultiple, apexMultiple + 1].flatMap((mult) =>
                    [apexWacc - 2, apexWacc, apexWacc + 2].map((wacc) =>
                      Math.max(0, (baseEbitda * mult * 12) * (1 - (wacc - 12.5) / 100) - deb)
                    )
                  );
                  const texto = interpretarSensibilidade({
                    apexMultiple, apexWacc, baseEbitda, debt: deb,
                    valorAtual: liveEquityValue,
                    valorMax: Math.max(...valoresMatriz),
                    valorMin: Math.min(...valoresMatriz),
                  });
                  return (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <p className="text-[11px] text-slate-400 leading-relaxed">{texto}</p>
                    </div>
                  );
                })()}

              </div>
            </div>
          )}
        </div>
      )}

      {/* ========================================== */}
      {/* ABA: ESTÚDIO WHITE LABEL (ESCADA DE LUXO)  */}
      {/* ========================================== */}
      {
        reportTab === 'whitelabel' && (
          <React.Fragment key="whitelabel-tab">
            <div className="w-full animate-fade-up">

              {/* 💡 CAIXA DE DICAS */}
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-6 flex flex-col gap-4 mb-4 text-left">
                <div className="flex gap-3 items-start">
                  <Crown size={16} className="text-purple-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-400 leading-relaxed">
                    <strong className="text-purple-400 block mb-0.5 uppercase tracking-wide">Estúdio de Customização:</strong>
                    Configure o nome corporativo e a identidade cromática primária para envelopar a geração do relatório executivo.
                  </p>
                </div>
                <div className="flex gap-3 items-start border-t border-purple-500/10 pt-3">
                  <Monitor size={16} className="text-purple-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-400 leading-relaxed">
                    <strong className="text-purple-400 block mb-0.5 uppercase tracking-wide">Ecossistema Phoenix OS:</strong>
                    O aplicativo <strong>Celular</strong> gerencia e aplica as regras visuais nos laudos de campo. Acesse sua Mesa pelo <strong>Computador (Desktop)</strong> para realizar uploads de logotipos em alta definição em vetor (.SVG).
                  </p>
                </div>
              </div>

              {/* ============================================================ */}
              {/* CONTROLE DE ACESSO - PAYWALL BOUTIQUE                         */}
              {/* ============================================================ */}
              {!hasAccess('enterprise') ? (
                /* 🔒 BOUTIQUE: Paywall Enterprise Ultra (R$ 3.997) */
                <div className="w-full bg-gradient-to-b from-slate-900/60 to-slate-950/40 backdrop-blur-3xl border border-white/10 p-8 md:p-12 rounded-[2.5rem] text-center shadow-[0_24px_60px_-15px_rgba(0,0,0,0.7)] relative overflow-hidden group">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-lime-600/10 rounded-full blur-[120px] pointer-events-none group-hover:scale-110 transition-transform duration-1000"></div>
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-lime-500/10 rounded-2xl flex items-center justify-center border border-lime-500/20 mx-auto mb-6 shadow-inner">
                      <Crown size={28} className="text-lime-400" />
                    </div>
                    <p className="text-[11px] text-purple-400/60 uppercase tracking-widest mb-1">Próximo degrau recomendado</p>
                    <p className="text-sm font-black text-purple-400 uppercase tracking-widest mb-1">Enterprise Ultra</p>
                    <div className="flex flex-row items-baseline justify-center gap-0.5 my-8 whitespace-nowrap">
                      <span className="text-xl font-black text-lime-400 italic mr-1">R$</span>
                      <span className="text-6xl font-black text-white tracking-tighter italic leading-none">3.997</span>
                      <span className="text-xl font-black text-white italic">,00</span>
                      <span className="text-[11px] text-slate-500 font-black uppercase ml-2">/ mês</span>
                    </div>
                    <p className="text-[11px] text-slate-400 font-bold tracking-normal mb-6 max-w-sm mx-auto leading-relaxed">
                      Entregue laudos com a marca da sua consultoria, carteira de CNPJs ilimitada e o motor de IA preditiva para projeções de longo prazo.
                    </p>
                    <p className="text-[11px] font-black uppercase tracking-widest text-lime-400 text-left max-w-sm mx-auto mb-3">Tudo do Boutique Elite, mais:</p>
                    <div className="flex flex-col gap-2 mb-6 text-left max-w-sm mx-auto">
                      {[
                        '🏷️ White-Label Completo (Nome, Logo e Cores)',
                        '📄 Super Relatório 360º com a sua Marca',
                        '🚀 Remoção da Assinatura Phoenix OS dos laudos',
                        '♾️ Carteira de CNPJs Ilimitada',
                        '🔭 IA Estocástica — Projeção +24 e 36 Meses',
                        '🤖 IA Preditiva avançada',
                        '🛡️ Suporte Dedicado com SLA de 99,9%'
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-2 bg-lime-500/5 border border-lime-500/10 rounded-xl px-3 py-2.5">
                          <span className="text-sm leading-none mt-0.5">{item.split(' ')[0]}</span>
                          <span className="text-[11px] font-bold text-slate-300 uppercase tracking-tight leading-tight">{item.split(' ').slice(1).join(' ')}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => typeof onUpgradeRequest === 'function' && onUpgradeRequest('enterprise')}
                      className="w-full py-4 rounded-full bg-lime-500 hover:bg-lime-400 text-black text-[11px] font-black uppercase tracking-widest shadow-[0_0_30px_rgba(132,204,22,0.4)] transition-all active:scale-95 flex items-center justify-center gap-2 mx-auto"
                    >
                      Assinar Plano <Zap size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                /* ============================================================ */
                /* CONTEÚDO LIBERADO - ENTERPRISE ULTRA                         */
                /* ============================================================ */
                <div className="w-full bg-gradient-to-br from-purple-900/10 to-transparent backdrop-blur-2xl border border-white/10 p-6 rounded-[2.5rem] shadow-2xl text-left">
                  <div className="space-y-5 mb-6">

                    {/* NOME DA CONSULTORIA */}
                    <div>
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">Nome da sua Consultoria de M&A</label>
                      <input
                        type="text"
                        value={wlName || ''}
                        onChange={(e) => setWlName && setWlName(e.target.value)}
                        placeholder="Ex: Sua Consultoria S/A"
                        className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white focus:outline-none focus:border-purple-500 transition-colors shadow-inner"
                      />
                    </div>

                    {/* COR PRIMÁRIA */}
                    <div>
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">Paleta de Cor Primária (Hexadecimal)</label>
                      <div className="flex gap-3 items-center">
                        <input
                          type="color"
                          value={wlColor || '#2563eb'}
                          onChange={(e) => setWlColor && setWlColor(e.target.value)}
                          className="w-12 h-12 rounded-xl bg-black/40 border border-white/10 cursor-pointer p-1 shrink-0"
                        />
                        <input
                          type="text"
                          value={wlColor || '#2563eb'}
                          onChange={(e) => setWlColor && setWlColor(e.target.value)}
                          className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white uppercase focus:outline-none focus:border-purple-500 transition-colors shadow-inner font-mono"
                        />
                      </div>
                    </div>

                    {/* UPLOAD DE LOGO */}
                    <div>
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">Logotipo da Consultoria</label>
                      <div className="flex items-center gap-3">
                        {wlLogo ? (
                          <img src={wlLogo} alt="Logo" className="w-14 h-14 object-contain rounded-xl border border-purple-500/30 bg-black/40 p-1 shrink-0" />
                        ) : (
                          <div className="w-14 h-14 rounded-xl border border-dashed border-white/20 bg-black/40 flex items-center justify-center shrink-0">
                            <Crown size={20} className="text-slate-600" />
                          </div>
                        )}
                        <label className="flex-1 cursor-pointer bg-black/40 border border-white/10 rounded-xl px-4 py-4 text-xs text-slate-400 hover:border-purple-500 transition-colors text-center font-black uppercase tracking-widest">
                          {wlLogo ? '✓ Logo Carregado — Trocar' : 'Carregar Logo (.PNG / .SVG)'}
                          <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                        </label>
                      </div>
                      {wlLogo && (
                        <p className="text-[11px] text-purple-400 font-bold mt-2 ml-1">✓ Logo aplicado ao relatório White-Label</p>
                      )}
                    </div>
                  </div>

                  {/* ============================================================ */}
                  {/* PREVIEW AO VIVO DA CAPA DO RELATÓRIO                          */}
                  {/* Atualiza sozinho conforme o Consultor digita/muda a cor/sobe o logo — */}
                  {/* sem precisar gerar o PDF inteiro pra ver o resultado.          */}
                  {/* ============================================================ */}
                  <div className="mb-6">
                    <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Preview da Capa (tempo real)</p>
                    <div
                      className="w-full aspect-[210/297] max-h-80 mx-auto rounded-xl overflow-hidden shadow-2xl relative bg-white flex flex-col"
                      style={{ border: `3px solid ${wlColor || '#2563eb'}` }}
                    >
                      {/* Faixa superior colorida */}
                      <div className="h-2.5 w-full shrink-0" style={{ backgroundColor: wlColor || '#2563eb' }}></div>

                      <div className="flex-1 flex flex-col justify-between p-5">
                        {/* Cabeçalho: logo + nome */}
                        <div className="flex items-center gap-2">
                          {wlLogo ? (
                            <img src={wlLogo} alt="Logo" className="w-8 h-8 object-contain shrink-0" />
                          ) : (
                            <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center" style={{ backgroundColor: `${wlColor || '#2563eb'}20` }}>
                              <Crown size={14} style={{ color: wlColor || '#2563eb' }} />
                            </div>
                          )}
                          <span className="text-[11px] font-black uppercase tracking-widest text-slate-700 truncate">
                            {wlName || 'Sua Consultoria S/A'}
                          </span>
                        </div>

                        {/* Bloco central: título do relatório */}
                        <div className="text-center py-4">
                          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Dossiê 360º</p>
                          <p className="text-lg font-black uppercase tracking-tight leading-tight" style={{ color: wlColor || '#2563eb' }}>
                            Relatório<br />Executivo
                          </p>
                          <div className="w-10 h-0.5 mx-auto mt-3" style={{ backgroundColor: wlColor || '#2563eb' }}></div>
                          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest mt-3">Empresa Exemplo Ltda.</p>
                        </div>

                        {/* Rodapé: data */}
                        <div className="flex items-center justify-between text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                          <span>Confidencial</span>
                          <span>{new Date().toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-500 text-center mt-2">Simulação ilustrativa — o layout final do PDF pode variar levemente.</p>
                  </div>

                  {/* SEPARADOR VISUAL */}
                  <div className="border-t border-white/5 pt-5">
                    <button
                      onClick={() => typeof onGenerateSuperReport === 'function' && onGenerateSuperReport()}
                      className="w-full py-5 px-6 rounded-2xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-widest shadow-[0_0_25px_rgba(168,85,247,0.3)] transition-all active:scale-95 flex items-center justify-center gap-2 text-center"
                    >
                      <Download size={16} className="shrink-0" /> <span>Emitir Super Relatório 360º White-Label</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </React.Fragment>
        )
      }

      {/* ========================================== */}
      {/* MODAL DE CRIAÇÃO DE CONTA PÓS-COMPRA */}
      {/* ========================================== */}
      <CreateAccountModal
        isOpen={showCreateAccount}
        onClose={() => setShowCreateAccount(false)}
        onSuccess={(userData) => {
          setShowCreateAccount(false);

          // 🔥 CORRIGIDO: não grava mais 'phoenix_consultor_plan' aqui — fazia
          // a UI mostrar o plano como ativo sem nenhum pagamento confirmado
          // (mesma vulnerabilidade corrigida em CreateAccountModal.jsx).
          if (typeof window !== 'undefined') {
            localStorage.setItem('phoenix_user_name', nome);
            localStorage.setItem('phoenix_user_email', email);
            localStorage.setItem('phoenix_consultor_email', email);
            localStorage.setItem('phoenix_user_type', 'consultor');
          }
        }}
        planId={pendingPlan?.id}
        planName={pendingPlan?.name}
        planPrice={pendingPlan?.price}
        formData={formData}
        report={report}
        finance={finance}
        dynamicInsights={dynamicInsights}
      />
    </div >
  );
}